import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../components/ui/toast/ToastContext';
import { useConfirm } from '../../components/ui/confirm/ConfirmContext';
import { fetchWithCsrf } from '../../utils/fetchWithCsrf';
import MultipleFileInput from '../../components/ui/buttons/MultipleFileInput';
import Button from '../../components/ui/buttons/Button';
import { Camera, FileText, User2, X } from 'lucide-react';
import { camelizeKeys } from '../../utils/camelizeKeys';
import Input from '../../components/ui/inputs/Input';
import { snakecaseKeys } from '../../utils/snakecaseKeys';
import PhoneInput from '../../components/ui/inputs/PhoneInput';
import CepInput from '../../components/ui/inputs/CepInput';

const Account = ({ data }) => {
  const { addToast } = useToast();
  const confirm = useConfirm();

  const [form, setForm] = useState({
    name: data.name || '',
    email: data.email || '',
    phone: data.phone || '',
    zipcode: data.zipcode || '',
    state: data.state || '',
    city: data.city || '',
    neighbourhood: data.neighbourhood || '',
    address: data.address || '',
    number: data.number || '',
    complement: data.complement || '',
    avatarUrl: data.avatarUrl || '',
  });

  const [isLoading, setIsLoading] = useState(false);

  const avatarInputRef = useRef(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);

    const payload = snakecaseKeys({ client: form });

    fetchWithCsrf('/app/client/account', {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) {
          const json = await res.json();
          throw new Error(json.errors?.join(', ') || 'Erro ao atualizar perfil.');
        }

        addToast({ type: 'success', message: 'Informações atualizadas com sucesso!' });
      })
      .catch((err) => {
        console.error('Erro ao atualizar perfil:', err);
        addToast({ type: 'error', message: err.message });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAvatarUpload = (e) => {
    e.preventDefault();

    setIsLoading(true);

    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);

    fetchWithCsrf('/app/client/account/avatar', {
      method: 'PUT',
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          const json = await res.json();
          throw new Error(json.errors?.join(', ') || 'Erro ao atualizar avatar.');
        }

        const newUrl = URL.createObjectURL(file);
        setForm((prev) => ({ ...prev, avatarUrl: newUrl }));

        addToast({ type: 'success', message: 'Avatar atualizado com sucesso!' });
      })
      .catch((err) => {
        console.error('Erro ao atualizar avatar:', err);
        addToast({ type: 'error', message: err.message });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="">
      <div className="grid gap-5 md:grid-cols-3">
        <form
          onSubmit={handleSubmit}
          className="rounded-xl p-5 bg-tealTransparent gap-5 col-span-2 flex flex-col gap-5"
        >
          <div className="bg-white rounded-custom20 p-8 flex flex-col gap-2">
            <h2 className="text-lg text-blueDarker font-semibold mb-4">Dados pessoais</h2>
            <div className="flex flex-col md:flex-row gap-5 md:gap-6 md:items-center">
              {/* avatar */}
              <div className="relative w-36 h-36 md:w-44 md:h-44 self-center md:self-start flex-shrink-0 aspect-square">
                {form.avatarUrl ? (
                  <img src={form.avatarUrl} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <div className="w-full h-full rounded-full bg-gray-100 flex items-center justify-center">
                    <User2 size={44} className="text-grayMedium" />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => avatarInputRef.current.click()}
                  className="flex items-center justify-center w-custom50 h-custom50 absolute -bottom-2 right-2 bg-white p-2 rounded-full hover:opacity-80"
                  aria-label="Atualizar avatar"
                  title="Atualizar avatar"
                >
                  <Camera size={28} strokeWidth={1.5} className="text-tealBase" />
                </button>

                <input
                  type="file"
                  accept="image/*"
                  ref={avatarInputRef}
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={isLoading}
                />
              </div>

              {/* inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
                <div className="md:col-span-2">
                  <Input disabled={isLoading} name="name" label="Nome" value={form.name} onChange={handleChange} />
                </div>
                <Input disabled name="email" label="Email" type="email" value={form.email} onChange={handleChange} />
                <PhoneInput
                  disabled={isLoading}
                  name="phone"
                  label="Telefone"
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-custom20 p-8 flex flex-col gap-2">
            <h2 className="text-lg text-blueDarker font-semibold mb-4">Endereço</h2>
            <div className="grid md:grid-cols-3 gap-5 flex-1">
              <CepInput disabled={isLoading} name="zipcode" label="CEP" value={form.zipcode} onChange={handleChange} />
              <Input disabled={isLoading} name="state" label="Estado" value={form.state} onChange={handleChange} />
              <Input disabled={isLoading} name="city" label="Cidade" value={form.city} onChange={handleChange} />
              <Input
                disabled={isLoading}
                name="neighbourhood"
                label="Bairro"
                value={form.neighbourhood}
                onChange={handleChange}
              />
              <Input
                disabled={isLoading}
                name="address"
                label="Endereço"
                value={form.address}
                onChange={handleChange}
              />
              <Input disabled={isLoading} name="number" label="Número" value={form.number} onChange={handleChange} />
              <div className="md:col-span-3">
                <Input
                  disabled={isLoading}
                  name="complement"
                  label="Complemento"
                  value={form.complement}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit">SALVAR ALTERAÇÕES</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Account;
